import React, { useCallback, useEffect, useMemo, useState } from 'react'
import B2BLayout from '../../../layouts/b2b'
import { Button, Form, Input, Popconfirm, Skeleton, Table, Tag, Tooltip, Typography } from 'antd'
import { EditOutlined, PlusOutlined, SaveOutlined, StopOutlined } from '@ant-design/icons'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { ButtonActionWrapper } from '../../../components/list-operations/styles'
import { useNavigate } from 'react-router-dom'
import { objectToUrl } from '../../utils'
import Result, { ResultStatusType } from 'antd/lib/result'
import FilteredSearch, { FilterKeyDef, FilteredSearchValue } from './filtered-search'

/** Ключи фильтра в URL: ?module=hr,claims&connected=1&q=текст */
const FILTER_PARAMS = ['module', 'connected', 'q'] as const

type ModuleFilterOption = { code: string; name: string; count: number; connected: boolean }

function readFilterFromUrl(search: string): FilteredSearchValue {
  const params = new URLSearchParams(search)
  const tokens: FilteredSearchValue['tokens'] = []
  for (const code of String(params.get('module') || '')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean)) {
    tokens.push({ key: 'module', value: code })
  }
  const connected = params.get('connected')
  if (connected === '1' || connected === '0') tokens.push({ key: 'connected', value: connected })
  return { tokens, search: String(params.get('q') || '') }
}

const requiredFields = ['name', 'code']
const EditableCell = ({
  editing,
  dataIndex,
  title,
  inputType,
  record,
  index,
  children,
  ...restProps
}) => {
  return (
    <td {...restProps}>
      {editing ? (
        <Form.Item
          name={dataIndex}
          style={{ margin: 0 }}
          rules={[
            {
              required: requiredFields.includes(dataIndex),
              message: `Обязательное поле ${title}!`,
            },
          ]}
        >
          <Input />
        </Form.Item>
      ) : (
        children
      )}
    </td>
  )
}

type TablePaginationPosition = 'bottomCenter'

interface IError {
  code?: ResultStatusType
  message?: string
}

const Collections = () => {
  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/admin',
        title: 'Админка',
      },
    ],
    [],
  )

  const [form] = Form.useForm()
  const [data, setData] = useState<any>([])
  const [editingKey, setEditingKey] = useState<null | number>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<null | IError>(null)
  const [sorter, setSorter] = useState<any>({})
  const [moduleOptions, setModuleOptions] = useState<ModuleFilterOption[]>([])
  // после первой загрузки не прячем страницу за Skeleton — иначе строка фильтра теряет фокус
  const [loadedOnce, setLoadedOnce] = useState(false)
  const [filter, setFilter] = useState<FilteredSearchValue>(() =>
    readFilterFromUrl(window.location.search),
  )
  const navigate = useNavigate()
  const [bottomCenter] = useState<TablePaginationPosition>('bottomCenter')
  const [pagination, setPagination] = useState<any>({
    current: 1,
    pageSize: 10,
    total: 10,
    position: [bottomCenter],
    showSizeChanger: false,
  })

  const isEditing = record => record.id === editingKey

  /**
   * Обновляет состояние пагинации
   */
  const updatePagination = useCallback(
    newState => {
      setPagination({
        ...pagination,
        ...newState,
      })
    },
    [pagination, setPagination],
  )

  useEffect(() => {
    getData()
  }, [])

  useEffect(() => {
    setPagination(cur => ({ ...cur, disabled: editingKey !== null }))
  }, [editingKey])

  /**
   * Получает записи
   */
  const getData = useCallback(async () => {
    setLoading(true)
    setError(null)
    const urlParams = Object.fromEntries(new URLSearchParams(location.search))
    await fetchAPI(`/api/enum-collections${objectToUrl(urlParams)}`)
      .then(({ data, pagination, sorter, filters }) => {
        if (data) {
          updatePagination(pagination)
          setSorter(sorter)
          setData(data)
        }
        if (Array.isArray(filters?.modules)) {
          setModuleOptions(filters.modules)
        }
      })
      .catch(e => {
        console.error('Ошибка при получении данных:', e)
        setError({
          message: (e as IHttpError)?.message || 'Неизвестная ошибка',
          code: ((e as IHttpError)?.statusCode || 'error') as ResultStatusType,
        })
      })
      .finally(() => {
        setLoading(false)
        setLoadedOnce(true)
      })
  }, [])

  const edit = record => {
    form.setFieldsValue({ name: '', code: '', module: '', ...record })
    setEditingKey(record.id)
  }

  const cancel = (id: number) => {
    if (id === 0) {
      setData(rows => rows.filter(item => item.id !== id))
    }
    form.setFieldsValue({ name: '', code: '', module: '' })
    setEditingKey(null)
  }

  const save = async id => {
    try {
      const row = await form.validateFields()
      const newData = [...data]
      const index = newData.findIndex(item => id === item.id)
      const newValue = newData[index]?.id === 0
      const valueId = newData[index]?.id
      if (index > -1) {
        const url = newValue ? '/api/enum-collection/new' : `/api/enum-collection/${valueId}`
        fetchAPI(url, {
          method: 'POST',
          body: JSON.stringify(row),
        })
          .then(result => {
            const { id, name, module, code } = result
            setData(data =>
              data.map(item => {
                if (item?.id === 0) item = { id, name, module, code }
                if (item?.id === id) item = { id, name, module, code }
                return item
              }),
            )
            setEditingKey(null)
            form.setFieldsValue({ name: '', code: '', module: '' })
            // перечитываем список: пересчитать модуль/фильтр для изменённой строки
            getData()
          })
          .catch(e => {
            console.error('Save error', { e })
          })
      }
    } catch (errInfo) {
      console.error('Validate Failed:', errInfo)
    }
  }

  /**
   * Отслеживает изменение состояния таблицы
   *
   * @param pagination
   * @param filters
   * @param sorter
   */
  const handleTableChange = (pagination, filters, sorter) => {
    const currentUrlParams = Object.fromEntries(new URLSearchParams(location.search))
    let current = {
      ...currentUrlParams,
      sort_order: sorter?.order,
      sort_field: sorter?.field,
      page: pagination.current,
    }
    if (current?.page === 1) {
      delete current.page
    }
    if (current?.sort_order === undefined || current?.sort_order === null) {
      delete current.sort_order
      delete current.sort_field
    }
    const urlParams = new URLSearchParams(current).toString()
    navigate({search: urlParams})
    getData()
  }

  /**
   * Применяет фильтр GitLab-style: пишет его в URL (сохраняя сортировку) и перезапрашивает данные
   */
  const applyFilter = useCallback(
    (next: FilteredSearchValue) => {
      setFilter(next)
      const current: Record<string, string> = Object.fromEntries(
        new URLSearchParams(location.search),
      )
      for (const key of FILTER_PARAMS) delete current[key]
      delete current.page
      const modules = next.tokens.filter(t => t.key === 'module').map(t => t.value)
      if (modules.length) current.module = modules.join(',')
      const connected = next.tokens.find(t => t.key === 'connected')?.value
      if (connected) current.connected = connected
      if (next.search.trim()) current.q = next.search.trim()
      navigate({ search: new URLSearchParams(current).toString() })
      getData()
    },
    [navigate, getData],
  )

  const filterKeys: FilterKeyDef[] = useMemo(
    () => [
      {
        key: 'module',
        title: 'Модуль',
        multiple: true,
        options: moduleOptions.map(m => ({
          value: m.code,
          label: m.name,
          count: m.count,
        })),
      },
    ],
    [moduleOptions],
  )

  const columns = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 60,
      render: v => (v !== 0 ? v : null),
      sorter: editingKey === null,
      sortOrder: sorter.sort_field === 'id' ? sorter.sort_order : null,
    },
    {
      title: 'Название',
      dataIndex: 'name',
      editable: true,
      sorter: editingKey === null,
      sortOrder: sorter.sort_field === 'name' ? sorter.sort_order : null,
    },
    {
      title: 'Код',
      dataIndex: 'code',
      width: 190,
      editable: true,
      sorter: editingKey === null,
      sortOrder: sorter.sort_field === 'code' ? sorter.sort_order : null,
    },
    {
      title: 'Модуль',
      dataIndex: 'module',
      width: 230,
      editable: true,
      sorter: editingKey === null,
      sortOrder: sorter.sort_field === 'module' ? sorter.sort_order : null,
      render: (value: string | null, record: any) => {
        if (!record?.platform_module || record.id === 0) return value
        const tag = (
          <Tag
            color={record.module_connected ? 'blue' : 'default'}
            style={{ cursor: 'pointer' }}
            onClick={e => {
              e.stopPropagation()
              if (editingKey !== null) return
              const exists = filter.tokens.some(
                t => t.key === 'module' && t.value === record.platform_module,
              )
              if (!exists) {
                applyFilter({
                  ...filter,
                  tokens: [...filter.tokens, { key: 'module', value: record.platform_module }],
                })
              }
            }}
          >
            {record.platform_module_name}
          </Tag>
        )
        return (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }}>
            {record.module_connected ? (
              tag
            ) : (
              <Tooltip title="Модуль не подключён у кабинета">{tag}</Tooltip>
            )}
            {value && value !== record.platform_module && (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {value}
              </Typography.Text>
            )}
          </span>
        )
      },
    },
    {
      title: 'Действия',
      dataIndex: 'operation',
      width: 110,
      style: '',
      render: (_: any, record) => {
        const editable = isEditing(record)
        return editable ? (
          <span
            style={{ display: 'flex', justifyContent: 'center' }}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Typography.Link onClick={() => save(record.id)} style={{ marginRight: 8 }}>
              <ButtonActionWrapper title="Редактировать">
                <SaveOutlined />
              </ButtonActionWrapper>
            </Typography.Link>
            <ButtonActionWrapper title="Сохранить">
              <Popconfirm
                title="Изменения не сохранятся, отменить?"
                onConfirm={() => cancel(record.id)}
              >
                <StopOutlined />
              </Popconfirm>
            </ButtonActionWrapper>
          </span>
        ) : (
          <span
            style={{ display: 'flex', justifyContent: 'center' }}
            onClick={e => {
              e.stopPropagation()
            }}
          >
            <Typography.Link disabled={editingKey !== null} onClick={() => edit(record)}>
              <ButtonActionWrapper title="Редактировать">
                <EditOutlined />
              </ButtonActionWrapper>
            </Typography.Link>
          </span>
        )
      },
    },
  ]

  const mergedColumns = columns.map(col => {
    if (!col.editable) return col

    return {
      ...col,
      onCell: record => ({
        record,
        inputType: 'text',
        dataIndex: col.dataIndex,
        title: col.title,
        editing: isEditing(record),
      }),
    }
  })

  const handleRowClick = useCallback(
    record => {
      if (editingKey === null) navigate(`/admin/enum-collection/${record.id}`)
    },
    [editingKey],
  )

  const addRow = useCallback(() => {
    setData(rows => {
      const newRow = {
        id: 0,
        name: null,
        code: null,
        module: null,
      }
      return [newRow, ...rows]
    })
    setEditingKey(0)
  }, [])

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && !loadedOnce && <Skeleton active />}
      {!loading && error && <Result status={error.code} title="Ошибка" subTitle={error.message} />}
      {loadedOnce && !error && (
        <>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: 10,
              alignItems: 'center',
            }}
          >
            <h3 style={{ fontWeight: 'bold', fontSize: 18, marginBottom: 0 }}>Коллекции</h3>
            <Button onClick={addRow} disabled={editingKey !== null}>
              <PlusOutlined /> Добавить коллекцию
            </Button>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 10 }}>
            <FilteredSearch
              filterKeys={filterKeys}
              value={filter}
              onChange={applyFilter}
              disabled={editingKey !== null}
              placeholder="Поиск по названию/коду или фильтр по модулю…"
            />
            <Typography.Text type="secondary" style={{ whiteSpace: 'nowrap' }}>
              Найдено: {data.filter(row => row?.id !== 0).length}
            </Typography.Text>
          </div>
          <Form form={form} component={false}>
            <Table
              loading={loading}
              components={{ body: { cell: EditableCell } }}
              size="small"
              dataSource={data}
              columns={mergedColumns}
              rowClassName={!editingKey ? 'editable-row cursor-pointer' : 'editable-row'}
              onRow={record => ({ onClick: () => handleRowClick(record) })}
              onChange={handleTableChange}
              pagination={false}
            />
          </Form>
        </>
      )}
    </B2BLayout>
  )
}

export default Collections
